import dayjs from "dayjs";
import { getLatestAIScreeningStatus } from "./interviewStatus";

export function transformProfile(profile: any) {
  const talent = Object.assign({}, profile);

  if (typeof talent.linkedin === "string" && talent.linkedin.startsWith("no-linkedin-")) {
    talent.linkedin = "";
  }

  const preferredTime = (hour: any) => {
    if (hour === null || hour === undefined) return null;
    const normalizedHour = hour === 24 ? 0 : hour;
    return dayjs().hour(normalizedHour).minute(0).format("HH:00");
  };

  const extractRecordingLink = (html: any) => {
    if (!html || typeof html !== "string") return null;

    const normalized = html.replace(/\n/g, " ");
    const blocks = normalized.split(/<\/p>|<\/li>|<br\s*\/?>/i);

    for (let i = 0; i < blocks.length; i++) {
      if (/Recording\s*:?/i.test(blocks[i])) {
        let match = blocks[i].match(/href=["']([^"']+)["']/i);
        if (match) return match[1];

        match = (blocks[i + 1] || "").match(/href=["']([^"']+)["']/i);
        if (match) return match[1];

        match = (blocks[i - 1] || "").match(/href=["']([^"']+)["']/i);
        if (match) return match[1];
      }
    }

    const hrefs = [...normalized.matchAll(/href=["']([^"']+)["']/gi)].map(
      (m) => m[1],
    );

    return (
      hrefs.find((url) =>
        /zoom\.us\/rec\/share|app\.fireflies\.ai\/view/i.test(url),
      ) || null
    );
  };

  const removeRecordingLink = (html: any) => {
    if (!html || typeof html !== "string") return html;

    const parts = html.split(/(<\/p>|<\/li>|<br\s*\/?>)/i);
    const cleaned: string[] = [];

    for (let i = 0; i < parts.length; i++) {
      const block = parts[i];
      if (/Recording\s*:?/i.test(block)) {
        const next = parts[i + 1] || "";
        if (/href=["'][^"']+["']/i.test(next)) {
          i++;
        }
        continue;
      }

      cleaned.push(block);
    }

    return cleaned
      .join("")
      .replace(/<li>\s*<\/li>/gi, "")
      .replace(/<p>\s*(<br\s*\/?>|\s)*<\/p>/gi, "")
      .trim();
  };

  const buildInterviewHistory = (interviews: any) => {
    const history = Array.isArray(interviews) ? interviews : [];
    const grouped: Record<string, any> = {};

    const hasValidInterviewData = (interview: any) => {
      if (!interview) return false;
      return (
        interview.feedback_comments &&
        interview.feedback_comments.trim() !== ""
      );
    };

    history.forEach((item) => {
      const {
        id,
        attempt_number,
        interview_type,
        feedback_rating,
        feedback_comments,
        scheduling_id,
        scheduling_start_time,
        interview_questions,
        reject_reason,
        created_at,
        updated_at,
      } = item;

      if (!grouped[attempt_number]) {
        grouped[attempt_number] = {
          attempt_number,
          technical: null,
          behavioral: null,
        };
      }

      grouped[attempt_number][interview_type] = {
        id,
        feedback_rating,
        feedback_comments,
        scheduling_id,
        recording_url: extractRecordingLink(feedback_comments),
        scheduling_start_time,
        interview_questions,
        reject_reason,
        created_at,
        updated_at,
      };
    });

    const sorted = Object.values(grouped).sort(
      (a: any, b: any) => b.attempt_number - a.attempt_number,
    );

    if (!sorted.length) return sorted;

    let latestIndex = sorted.findIndex(
      (a: any) =>
        hasValidInterviewData(a.technical) ||
        hasValidInterviewData(a.behavioral),
    );

    if (latestIndex === -1) return sorted;

    const latest: any = sorted[latestIndex];

    if (!hasValidInterviewData(latest.technical)) {
      for (let i = latestIndex + 1; i < sorted.length; i++) {
        if (hasValidInterviewData((sorted[i] as any).technical)) {
          latest.technical = (sorted[i] as any).technical;
          break;
        }
      }
    }

    if (!hasValidInterviewData(latest.behavioral)) {
      for (let i = latestIndex + 1; i < sorted.length; i++) {
        if (hasValidInterviewData((sorted[i] as any).behavioral)) {
          latest.behavioral = (sorted[i] as any).behavioral;
          break;
        }
      }
    }

    sorted.splice(latestIndex, 1);
    sorted.unshift(latest);

    return sorted;
  };

  const preferredDays = (d: any) => {
    const days = ["Sun", "M", "T", "W", "Th", "F", "S"];
    return (Array.isArray(d) ? d : [])
      .map((value, index) => (value ? days[index] : null))
      .filter(Boolean);
  };

  const sortExperience = (e: any) => {
    if (!Array.isArray(e)) return [];

    return [...e].sort((a, b) => {
      const aCurrent = !a.end_date;
      const bCurrent = !b.end_date;

      if (aCurrent && !bCurrent) return -1;
      if (!aCurrent && bCurrent) return 1;

      const aEnd = a.end_date ? new Date(a.end_date) : new Date();
      const bEnd = b.end_date ? new Date(b.end_date) : new Date();

      if (bEnd.getTime() - aEnd.getTime() !== 0) return bEnd.getTime() - aEnd.getTime();

      return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
    });
  };

  const groupTags = (exp: any) => {
    const tags: any[] = [];
    exp.forEach((e: any) => {
      if (e.tags && Array.isArray(e.tags)) {
        tags.push(...e.tags);
      }
    });

    const uniqueTags = new Map();
    tags.forEach((t) => {
      const tagProficiency = t.proficiency;
      const tagId = t.tag_id;

      const existingUniqueTag = uniqueTags.get(tagId);
      if (!existingUniqueTag || tagProficiency > existingUniqueTag.proficiency) {
        uniqueTags.set(tagId, t);
      }
    });

    return [...uniqueTags.values()].sort((a: any, b: any) => {
      if (b.proficiency !== a.proficiency) {
        return b.proficiency - a.proficiency;
      }
      return a.name.localeCompare(b.name);
    });
  };

  const groupExperience = (e: any) => {
    if (!e) {
      return {};
    }
    return Object.values(
      e.reduce((acc: any, item: any) => {
        const key = item.company_name;
        if (!acc[key])
          acc[key] = { company_name: key, experiences: [], tags: [] };
        acc[key].experiences.push(item);
        acc[key].tags.push(...(item.tags || []));
        return acc;
      }, {}),
    );
  };

  const getStageDepricated = (row: any) => {
    // Dormant STAGE enums win over the flag-derived legacy status (2026-08-03).
    // Without this, a candidate parked at stage='backlog' whose old flags still
    // say application_approved + interview_stage='behavioral' rendered as
    // "Interviewing - Behavioral" while auto-cancel bounced every booking —
    // the display lie behind the Faiz/Veronica L3 confusion.
    if (row.stage === 'backlog') return "backlog";
    if (row.stage === 'inactive') return "inactive";
    if (row.stage === 'passive') return "passive";
    if (row.stage === 'matched') return "matched";
    return row.is_inactive
      ? "inactive"
      : row.cooldown_until
        ? "cooldown"
        : row.stage === 'active'
          ? "active"
          : row.stage === 'approved'
            ? "onboarding"
            : row.application_approved
              ? `interviewing ${row.interview_stage ? "- " + row.interview_stage : ""}`
              : "applying";
  };

  // parseLatestAi + getLatestAIScreeningStatus now come from ./interviewStatus
  // (the same implementation the talent LIST pages use) so the profile's
  // interview_ai_screening can't diverge from the list. The old local copy
  // emitted status "Failed"; the shared one emits "Incomplete" — no consumer
  // branches on "Failed" (TechnicalInterviewModal checks Completed/success/
  // failure; the list path already produced "Incomplete").

  const getApplicationStatus = (rawStage: string | null | undefined) => {
    if (!rawStage) return null;

    // Dormant stage enums first — stage is authoritative (2026-08-03).
    if (rawStage === "backlog") return "backlog";
    if (rawStage === "inactive") return "inactive";
    if (rawStage === "passive") return "passive";

    // Inactive / passive — show dedicated banner instead of application status
    if (talent.is_inactive) {
      const notesStr = typeof talent.metadata_notes === 'string'
        ? talent.metadata_notes.toLowerCase()
        : JSON.stringify(talent.metadata_notes ?? '').toLowerCase();
      if (notesStr.includes('passive')) return "passive";
      return "inactive";
    }

    if (talent.cooldown_until) {
      return "application_rejected";
    }

    if (rawStage === "application_draft") return "application_draft";

    if (
      rawStage === "application_awaiting_review" ||
      rawStage === "application_approved_pending"
    ) {
      return "pending";
    }

    if (
      rawStage === "application_rejected" ||
      rawStage === "application_rejected_pending"
    ) {
      return "application_rejected";
    }

    if (
      rawStage.startsWith("technical_interview_rejected") ||
      rawStage.startsWith("behavioral_interview_rejected")
    ) {
      return "application_rejected";
    }

    if (
      rawStage.startsWith("technical_interview_") ||
      rawStage.startsWith("behavioral_interview_")
    ) {
      return "approved";
    }

    if (rawStage === "approved") return "approved";

    return null;
  };

  const sortedWorkExperience = sortExperience(talent.work_experiences);
  const modifiedWorkExperience = groupExperience(sortedWorkExperience);

  return {
    ...talent,
    raw_stage: talent.stage,
    stage: getStageDepricated(talent),
    application_status: getApplicationStatus(talent.stage),
    name: `${talent.first_name} ${talent.last_name}`,
    preferred_working_days: preferredDays(talent.preferred_working_days),
    preferred_ending_hour: preferredTime(talent.preferred_ending_hour),
    preferred_starting_hour: preferredTime(talent.preferred_starting_hour),
    work_experiences: sortedWorkExperience,
    work_exp_modified: modifiedWorkExperience,
    work_exp_tags: groupTags(sortedWorkExperience),
    interview_ai_screening: getLatestAIScreeningStatus(talent),
    interview_type: talent.ai_screening ? ("ai" as const) : ("manual" as const),
    interview_history: buildInterviewHistory(talent.interviews),
    remove_recording_link: removeRecordingLink,
    revet: talent.revet ?? null,
    revets: Array.isArray(talent.revets) ? talent.revets : null,
    sample_works: Array.isArray(talent.sample_works) ? talent.sample_works : [],
  };
}
